import {
  signUp,
  signIn,
  signOut,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  fetchAuthSession,
} from 'aws-amplify/auth';
import queryString from 'query-string';
import sigV4Client from './sigv4Client';
import { AUTH_INVALID_CODE } from './constants';
import InvalidMfaCodeError from '../models/errors/invalid-mfa-code-error';

// ---------------------------------------------------------------------------
// Auth state constants
// ---------------------------------------------------------------------------
export const AuthState = {
  SignIn: 'signin',
  SignedIn: 'signedin',
  SignedOut: 'signedout',
  ConfirmSignIn: 'confirmsignin',
  ResetPassword: 'resetpassword',
  ForgotPassword: 'forgotpassword',
  TOTPSetup: 'totpsetup',
  ConfirmSignUp: 'confirmsignup',
};

// TOTP MFA step constants returned by Cognito
export const MFA_STEPS = {
  TOTP_CODE: 'CONFIRM_SIGN_IN_WITH_TOTP_CODE',
  TOTP_SETUP: 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP',
  NEW_PASSWORD: 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED',
  CUSTOM_CHALLENGE: 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE',
};

// ---------------------------------------------------------------------------
// Auth state event bus — single listener, fires synchronously
// ---------------------------------------------------------------------------
let _authListener = null;

export const AUTH_CHANNEL = 'UI Auth';
export const AUTH_STATE_CHANGE_EVENT = 'AuthStateChange';

export const onAuthUIStateChange = (callback) => {
  _authListener = callback;
  return () => { _authListener = null; };
};

export const changeAuthState = (nextState, data) => {
  if (_authListener) _authListener(nextState, data);
};

// ---------------------------------------------------------------------------
// Sign up
// ---------------------------------------------------------------------------
export const cognitoSignUp = async (username, password, attributes) => {
  const { userId } = await signUp({ username, password, options: { userAttributes: attributes } });
  changeAuthState(AuthState.ConfirmSignUp, { username });
  return { username };
};

// ---------------------------------------------------------------------------
// Sign in — SRP auth with TOTP MFA
// ---------------------------------------------------------------------------
export const cognitoSignIn = async (username, password) => {
  try {
    const output = await signIn({ username, password, options: { authFlowType: 'USER_SRP_AUTH' } });
    console.log('cognitoSignIn output:', JSON.stringify(output, null, 2));

    const signInStep = output.nextStep?.signInStep;

    if (signInStep === MFA_STEPS.NEW_PASSWORD) {
      changeAuthState(AuthState.ResetPassword, { ...output, username });
    } else if (signInStep === MFA_STEPS.TOTP_CODE) {
      // Existing enrolled user — prompt for authenticator app code
      changeAuthState(AuthState.ConfirmSignIn, output);
    } else if (signInStep === MFA_STEPS.TOTP_SETUP) {
      // First-time user — must enroll authenticator app
      // Attach username to output so the setup component can build the QR URI
      // without calling getCurrentUser() (which fails mid-auth)
      changeAuthState(AuthState.TOTPSetup, { ...output, username });
    } else if (output.isSignedIn) {
      const session = await fetchAuthSession();
      return { isSignedIn: true, session };
    }

    return output;
  } catch (err) {
    console.log('cognitoSignIn error:', err);
    throw err;
  }
};

// ---------------------------------------------------------------------------
// Complete new password
// ---------------------------------------------------------------------------
export const cognitoCompleteNewPassword = async (newPassword, username) => {
  try {
    const output = await confirmSignIn({ challengeResponse: newPassword });
    const signInStep = output.nextStep?.signInStep;
    if (signInStep === MFA_STEPS.TOTP_SETUP) {
      changeAuthState(AuthState.TOTPSetup, { ...output, username });
    } else if (signInStep === MFA_STEPS.TOTP_CODE) {
      changeAuthState(AuthState.ConfirmSignIn, output);
    } else if (signInStep === MFA_STEPS.CUSTOM_CHALLENGE) {
      changeAuthState(AuthState.ConfirmSignIn, output);
    } else if (output.isSignedIn) {
      const session = await fetchAuthSession();
      changeAuthState(AuthState.SignedIn, session);
    }
    return output;
  } catch (err) {
    throw err;
  }
};

// ---------------------------------------------------------------------------
// Confirm sign-in (TOTP code from authenticator app)
// ---------------------------------------------------------------------------
export const cognitoConfirmSignIn = async (_user, totpCode) => {
  try {
    const output = await confirmSignIn({ challengeResponse: totpCode });
    const signInStep = output.nextStep?.signInStep;

    if (signInStep === MFA_STEPS.TOTP_SETUP) {
      changeAuthState(AuthState.TOTPSetup, output);
    } else if (signInStep === MFA_STEPS.NEW_PASSWORD) {
      changeAuthState(AuthState.ResetPassword, output);
    } else if (output.isSignedIn) {
      const session = await fetchAuthSession();
      changeAuthState(AuthState.SignedIn, session);
    }
    return output;
  } catch (err) {
    // CodeMismatchException = wrong code, still retryable
    if (err.name === 'CodeMismatchException' || err.name === 'EnableSoftwareTokenMFAException') {
      throw new InvalidMfaCodeError(AUTH_INVALID_CODE);
    }
    // NotAuthorizedException after repeated failures = locked out
    throw err;
  }
};

// ---------------------------------------------------------------------------
// TOTP setup — complete enrollment during CONTINUE_SIGN_IN_WITH_TOTP_SETUP
// sharedSecret is already provided by Cognito in the signIn nextStep,
// so we only need to confirm sign-in with the TOTP code the user enters.
// ---------------------------------------------------------------------------
export const cognitoVerifyTOTPSetup = async (totpCode) => {
  // confirmSignIn with the TOTP code completes the CONTINUE_SIGN_IN_WITH_TOTP_SETUP
  // challenge — Cognito registers the device and issues tokens in one step.
  const output = await confirmSignIn({ challengeResponse: totpCode });
  return output;
};

// ---------------------------------------------------------------------------
// Sign out
// ---------------------------------------------------------------------------
export const cognitoSignOut = async () => {
  try { await signOut(); } catch (err) { /* ignore */ }
  changeAuthState(AuthState.SignedOut);
};

// ---------------------------------------------------------------------------
// Forgot password
// ---------------------------------------------------------------------------
export const cognitoSendCode = async (username) => resetPassword({ username });

export const cognitoForgotPassword = async (username, code, password) =>
  confirmResetPassword({ username, confirmationCode: code, newPassword: password });

// ---------------------------------------------------------------------------
// Public session helpers
// ---------------------------------------------------------------------------
export const authenticatePublicSession = (sessionData) => {
  const creds = queryString.parse(atob(sessionData));
  const tokenPrefix = 'DigitalPortalPublicUser';
  const referenceNum = atob(creds.sessionId).split(':')[1];
  localStorage.setItem(`${tokenPrefix}SessionId`, creds.sessionId.replace(/ /g, '+'));
  localStorage.setItem(`${tokenPrefix}AccessId`, creds.accessId.replace(/ /g, '+'));
  localStorage.setItem(`${tokenPrefix}SecretKey`, creds.secretKey.replace(/ /g, '+'));
  localStorage.setItem(`${tokenPrefix}SessionToken`, creds.sessionToken.replace(/ /g, '+'));
  localStorage.setItem(`${tokenPrefix}TransactionId`, referenceNum);
  localStorage.setItem('PUBLIC_SESSION', 'true');
};

export const currentPublicSessionCredentials = () => {
  const p = 'DigitalPortalPublicUser';
  return {
    accessKeyId: localStorage.getItem(`${p}AccessId`),
    secretAccessKey: localStorage.getItem(`${p}SecretKey`),
    sessionToken: localStorage.getItem(`${p}SessionToken`),
  };
};

export const getSessionId = () => localStorage.getItem('DigitalPortalPublicUserSessionId');
export const getTransactionId = () => localStorage.getItem('DigitalPortalPublicUserTransactionId');

// ---------------------------------------------------------------------------
// SigV4 signer
// ---------------------------------------------------------------------------
export const sign = (credentials, method, endpoint, path, queryParams = null, body = null, headers = {}) => {
  return sigV4Client
    .newClient({
      accessKey: credentials.accessKeyId,
      secretKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
      region: 'ap-southeast-1',
      endpoint,
    })
    .signRequest({ method, path, headers, queryParams, body }).headers;
};

// ---------------------------------------------------------------------------
// Token / role helpers
// ---------------------------------------------------------------------------
export const getAuthenticatedRole = async () => {
  try {
    const session = await fetchAuthSession();
    const groups = session.tokens?.idToken?.payload?.['cognito:groups'];
    return groups && groups.length ? groups[0] : '';
  } catch {
    return '';
  }
};

export const getAuthenticatedUserAttributes = async () => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.payload ?? {};
  } catch {
    return {};
  }
};

export const getAuthenticatedIdToken = async () => {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
};
